import FoilFrame from '@/components/ui/FoilFrame'
import GameBadge from '@/components/ui/GameBadge'
import type { Card } from '@/hooks/useCards'
import { isAuctionListing } from '@/lib/games'
import {ExternalLink, Pencil, Tag, Trash2} from 'lucide-react'
import { Link } from 'react-router-dom'

interface CardTileProps {
  card: Card
  showOwnerActions?: boolean
  storeName?: string
  storeHref?: string
  storeSlug?: string
  storeUserId?: string
  onEdit?: () => void
  onDelete?: () => void
}

function CardTile({ card, showOwnerActions, storeName, storeHref, storeSlug, storeUserId, onEdit, onDelete }: CardTileProps) {
  const href = storeHref || (storeSlug ? `/${storeSlug}` : storeUserId ? `/store/${storeUserId}` : undefined)

  return (
    <FoilFrame className="group h-full">
      <div className="flex h-full flex-col overflow-hidden rounded-[22px]">
        <div className="relative aspect-[3/4] w-full overflow-hidden bg-gradient-to-br from-[#1a0a0a] to-black">
          {card.imageUrl ? (
            <img
              src={card.imageUrl}
              alt={card.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-white/20">
              <Tag className="h-8 w-8" />
            </div>
          )}
          <div className="absolute left-2 top-2">
            <GameBadge listingType={card.listingType} productType={card.productType} />
          </div>
          {isAuctionListing(card.saleType) ? (
            <div className="absolute right-2 top-2 rounded-full bg-[#FF1A1A] px-2.5 py-1 text-xs font-extrabold uppercase tracking-wide text-white shadow-[0_3px_0_#9d1010]">
              Auction
            </div>
          ) : card.forSale && card.price != null ? (
            <div className="absolute right-2 top-2 rounded-full bg-[#FFDE00] px-2.5 py-1 text-xs font-extrabold text-black shadow-[0_3px_0_#c9a800]">
              ${Number(card.price).toFixed(2)}
            </div>
          ) : null}
          {showOwnerActions ? (
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-end gap-1.5 bg-gradient-to-t from-black/90 to-transparent p-2">
              <button
                type="button"
                onClick={onEdit}
                className="rounded-full bg-white/15 p-1.5 text-white hover:bg-white/25"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="rounded-full bg-[#FF1A1A]/80 p-1.5 text-white hover:bg-[#FF1A1A]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
        </div>

        <div className="flex flex-1 flex-col gap-2 p-3">
          <h3 className="font-display line-clamp-1 text-base font-extrabold text-white">{card.title}</h3>
          {card.condition ? <p className="text-[11px] font-extrabold uppercase tracking-wide text-white/40">{card.condition}</p> : null}

          {storeName ? (
            href ? (
              <Link to={href} className="text-xs font-extrabold text-[#8EA2FF] hover:text-[#FFDE00]">
                {storeName}
              </Link>
            ) : (
              <span className="text-xs font-extrabold text-[#8EA2FF]">{storeName}</span>
            )
          ) : null}

          {card.description ? (
            <p className="line-clamp-2 text-xs font-semibold text-white/45">{card.description}</p>
          ) : null}

          {(card.tcgplayerUrl || card.ebayUrl) ? (
            <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
              {card.tcgplayerUrl ? (
                <a
                  href={card.tcgplayerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 rounded-full border-2 border-[#3B4CCA] bg-[#3B4CCA]/20 px-2.5 py-1 text-[11px] font-extrabold text-white transition-transform hover:-translate-y-0.5"
                >
                  TCGplayer
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              ) : null}
              {card.ebayUrl ? (
                <a
                  href={card.ebayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 rounded-full border-2 border-[#FFDE00] bg-[#FFDE00]/15 px-2.5 py-1 text-[11px] font-extrabold text-[#FFDE00] transition-transform hover:-translate-y-0.5"
                >
                  eBay
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </FoilFrame>
  )
}

export default CardTile
